import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  username: '',
  patientId: '',
  currentUser: { id: '', djangoUserId: null, username: '', email: '', firstName: '', lastName: '', tokenType: '', userType: '', isAnkrAdmin: false, isSalesUser: false, isHealthcareUser: false, isPatientUser: false, role: '', department: '', title: '', phone: '', phone2: '', timezone: '', teamId: '', teamName: '', organizationId: '', organizationName: '', healthSystemMemberships: [], isActive: true },
  patientInfo: {
    firstName: '',
    lastName: '',
    dob: '',
    id: '',
    email: '',
    primaryNumber: '',
    alternateNumber: ''
  },
  providerInfo: {
    firstName: '',
    lastName: '',
    id: '',
    email: '',
    mobileNumber: ''
  },
  healthcareSystemInfo: {
    id: '',
    name: '',
    location: ''
  },
  healthSystems: [], // Array of health systems from JWT
  caseInfo: {
    coreServerCaseID: '',
    miraSolCaseID: ''
  },
  insuranceInfo: {
    id: '',
    provider: '',
    groupNumber: '',
    memberId: '',
    isVerified: false,
    verificationDate: null
  },
  appointmentInfo: {
    id: '',
    ehrAppointmentId: '',
    appointmentType: '',
    status: '',
    startDateTime: null,
    endDateTime: null,
    minutesDuration: null,
    timezone: '',
    reason: '',
    priority: '',
    providerName: '',
    departmentName: '',
    clinicName: '',
    notes: '',
    patientInstructions: ''
  }
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUsername: (state, action) => {
      state.username = action.payload;
    },
    clearUsername: (state) => {
      state.username = '';
    },
    setPatientId: (state, action) => {
      state.patientId = action.payload;
    },
    clearPatientId: (state) => {
      state.patientId = '';
    },
    setTokenData: (state, action) => {
      const tokenData = action.payload;

      // Update patient info
      if (tokenData.patient_info) {
        state.patientInfo = {
          firstName: tokenData.patient_info.first_name || '',
          lastName: tokenData.patient_info.last_name || '',
          dob: tokenData.patient_info.dob || '',
          id: tokenData.patient_info.id || '',
          email: tokenData.patient_info.email || '',
          primaryNumber: tokenData.patient_info.primary_number || '',
          alternateNumber: tokenData.patient_info.alternate_number || ''
        };
      }

      // Update provider info
      if (tokenData.provider_info) {
        state.providerInfo = {
          firstName: tokenData.provider_info.first_name || '',
          lastName: tokenData.provider_info.last_name || '',
          id: tokenData.provider_info.id || '',
          email: tokenData.provider_info.email || '',
          mobileNumber: tokenData.provider_info.mobile_number || ''
        };
      }

      // Update healthcare system info (single system for patient tokens)
      if (tokenData.healthcare_system_info) {
        state.healthcareSystemInfo = {
          id: tokenData.healthcare_system_info.id || '',
          name: tokenData.healthcare_system_info.name || '',
          location: tokenData.healthcare_system_info.location || ''
        };
      }

      // Update health systems (multi-system for admin/healthcare user tokens)
      if (tokenData.health_systems && Array.isArray(tokenData.health_systems)) {
        state.healthSystems = tokenData.health_systems;
      }
      
      // Update case info
      if (tokenData.case_info) {
        state.caseInfo = {
          coreServerCaseID: tokenData.case_info.CoreServer_CaseID || '',
          miraSolCaseID: tokenData.case_info.MiraSol_CaseID || ''
        };
      }
      
      // Update insurance info
      if (tokenData.insurance_info) {
        state.insuranceInfo = {
          id: tokenData.insurance_info.id || '',
          provider: tokenData.insurance_info.provider || '',
          groupNumber: tokenData.insurance_info.group_number || '',
          memberId: tokenData.insurance_info.member_id || '',
          isVerified: tokenData.insurance_info.is_verified || false,
          verificationDate: tokenData.insurance_info.verification_date || null
        };
      }
      
      // Update appointment info
      if (tokenData.appointment) {
        state.appointmentInfo = {
          id: tokenData.appointment.id || '',
          ehrAppointmentId: tokenData.appointment.ehr_appointment_id || '',
          appointmentType: tokenData.appointment.appointment_type || '',
          status: tokenData.appointment.status || '',
          startDateTime: tokenData.appointment.start_datetime || null,
          endDateTime: tokenData.appointment.end_datetime || null,
          minutesDuration: tokenData.appointment.minutes_duration || null,
          timezone: tokenData.appointment.timezone || '',
          reason: tokenData.appointment.reason || '',
          priority: tokenData.appointment.priority || '',
          providerName: tokenData.appointment.provider_name || '',
          departmentName: tokenData.appointment.department_name || '',
          clinicName: tokenData.appointment.clinic_name || '',
          notes: tokenData.appointment.notes || '',
          patientInstructions: tokenData.appointment.patient_instructions || ''
        };
      }
      
      // Also update legacy fields for backward compatibility
      state.patientId = tokenData.patient_id || tokenData.patient_info?.id || '';
      state.username = tokenData.patient_info ?
        `${tokenData.patient_info.first_name || ''} ${tokenData.patient_info.last_name || ''}`.trim() : '';
    },
    clearTokenData: (state) => {
      state.patientInfo = { firstName: '', lastName: '', dob: '', id: '', email: '', primaryNumber: '', alternateNumber: '' };
      state.providerInfo = { firstName: '', lastName: '', id: '', email: '', mobileNumber: '' };
      state.healthcareSystemInfo = { id: '', name: '', location: '' };
      state.healthSystems = [];
      state.caseInfo = { coreServerCaseID: '', miraSolCaseID: '' };
      state.insuranceInfo = { id: '', provider: '', groupNumber: '', memberId: '', isVerified: false, verificationDate: null };
      state.appointmentInfo = { id: '', ehrAppointmentId: '', appointmentType: '', status: '', startDateTime: null, endDateTime: null, minutesDuration: null, timezone: '', reason: '', priority: '', providerName: '', departmentName: '', clinicName: '', notes: '', patientInstructions: '' };
      state.username = '';
      state.patientId = '';
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    clearCurrentUser: (state) => {
      state.currentUser = { id: '', djangoUserId: null, username: '', email: '', firstName: '', lastName: '', tokenType: '', userType: '', isAnkrAdmin: false, isSalesUser: false, isHealthcareUser: false, isPatientUser: false, role: '', department: '', title: '', phone: '', phone2: '', timezone: '', teamId: '', teamName: '', organizationId: '', organizationName: '', healthSystemMemberships: [], isActive: true };
    },
  },
});

// Export actions
export const { setUsername, clearUsername, setPatientId, clearPatientId, setTokenData, clearTokenData, setCurrentUser, clearCurrentUser } = userSlice.actions;

// Export selectors
export const selectUsername = (state) => state.user.username;
export const selectPatientId = (state) => state.user.patientId;
export const selectCurrentUser = (state) => state.user.currentUser;
export const selectPatientInfo = (state) => state.user.patientInfo;
export const selectProviderInfo = (state) => state.user.providerInfo;
export const selectHealthcareSystemInfo = (state) => state.user.healthcareSystemInfo;
export const selectHealthSystems = (state) => state.user.healthSystems;
export const selectCaseInfo = (state) => state.user.caseInfo;
export const selectInsuranceInfo = (state) => state.user.insuranceInfo;
export const selectAppointmentInfo = (state) => state.user.appointmentInfo;

// Export reducer
export default userSlice.reducer;
